/* Custos extras padrão: linhas livres que entram em todos os projetos. O nome
 * é editado inline; a regra abre no mesmo modal das linhas fixas. */

import { custoConfig } from '@favo/core';
import { Plus, Settings2, X } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/componentes/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card';
import { Input } from '@/componentes/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/componentes/ui/table';
import { Valor } from '@/componentes/valor';
import type { ConfigCustoExtra } from '@/dados';

import { descreverRegra, type Edicao } from './configuracao';

export function Extras({
  extras,
  kwpRef,
  modulosRef,
  focoNoExtra,
  aoConfigurar,
  aoRenomear,
  aoRemover,
  aoAdicionar,
}: {
  extras: ConfigCustoExtra[];
  kwpRef: number | null;
  modulosRef: number | null;
  /** Id do extra recém-criado, para o campo de nome nascer com o cursor dentro. */
  focoNoExtra: React.RefObject<string | null>;
  aoConfigurar: (edicao: Edicao) => void;
  aoRenomear: (id: string, nome: string) => void;
  aoRemover: (id: string) => void;
  aoAdicionar: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Custos extras padrão</CardTitle>
        <CardDescription>
          Custos que entram em todos os projetos e não estão na lista acima, como frete ou
          laudo técnico.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {extras.length === 0 ? (
          <p className="text-muted-foreground text-apoio">
            Nenhum custo extra. Os seis custos acima já cobrem a maioria dos projetos.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Regra</TableHead>
                <TableHead className="text-right">No exemplo</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {extras.map((extra) => (
                <TableRow key={extra.id}>
                  <TableCell>
                    <Input
                      defaultValue={extra.label ?? ''}
                      placeholder="Nome do custo"
                      aria-label="Nome do custo extra"
                      className="h-8 w-full min-w-40"
                      ref={(elemento) => {
                        if (elemento && focoNoExtra.current === extra.id) {
                          focoNoExtra.current = null;
                          elemento.focus();
                        }
                      }}
                      onBlur={(evento) =>
                        aoRenomear(extra.id ?? '', evento.currentTarget.value.trim())
                      }
                      onKeyDown={(evento) => {
                        if (evento.key === 'Enter') evento.currentTarget.blur();
                      }}
                    />
                  </TableCell>
                  <TableCell className="whitespace-normal">
                    <span
                      className={
                        extra.modo ? 'text-apoio' : 'text-muted-foreground text-apoio italic'
                      }
                    >
                      {descreverRegra(extra)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Valor
                      valor={custoConfig(extra, kwpRef ?? 0, modulosRef ?? 0)}
                      formato="moeda"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="outline"
                        size="icon-sm"
                        aria-label={`Configurar ${extra.label || 'custo extra'}`}
                        onClick={() =>
                          aoConfigurar({
                            chave: `extra:${extra.id ?? ''}`,
                            titulo: extra.label || 'Custo extra',
                            cfg: extra,
                            configurado: false,
                          })
                        }
                      >
                        <Settings2 />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remover ${extra.label || 'custo extra'}`}
                        onClick={() => aoRemover(extra.id ?? '')}
                      >
                        <X />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <div>
          <Button variant="outline" size="sm" onClick={aoAdicionar}>
            <Plus />
            Adicionar custo extra
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
